import { useEffect, useRef, type JSX } from 'react';

import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchUserDetails,
  logout,
  selectUser,
} from '../modules/user/profileSlice';
import Forbidden from '../ui-components/Forbidden';

export interface AuthGuardProps {
  required?: string | string[];
  children: JSX.Element;
}

const AuthGuard = ({
  required,
  children,
}: AuthGuardProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectUser) as {
    permissions?: unknown;
    groupCode?: string;
  } | null;
  const isLoading = useAppSelector((state) => state.profile.loading);
  const error = useAppSelector((state) => state.profile.error);
  const lastTokenRef = useRef<string | null>(null);
  const didRedirectRef = useRef(false);
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (!token) {
      lastTokenRef.current = null;
      if (!didRedirectRef.current) {
        didRedirectRef.current = true;
        dispatch(logout());
        navigate('/login', { state: { from: location } });
      }
      return;
    }

    didRedirectRef.current = false;

    if (!user && !isLoading) {
      if (error && lastTokenRef.current === token) {
        return;
      }
      lastTokenRef.current = token;
      dispatch(fetchUserDetails())
        .unwrap()
        .catch((err) => {
          if (err === 'Unauthorized' || err === 'Forbidden') {
            dispatch(logout());
            navigate('/login', { state: { from: location } });
          }
        });
    }
  }, [dispatch, navigate, user, location, isLoading, error, token]);

  const permissions: unknown = user?.permissions ?? [];
  const roleCode =
    typeof user?.groupCode === 'string'
      ? user.groupCode.trim().toUpperCase()
      : '';
  const permissionList = (() => {
    if (Array.isArray(permissions)) {
      return permissions;
    }
    if (typeof permissions === 'string') {
      const trimmed = permissions.trim();
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
    }
    return [];
  })();

  const hasPermission: unknown = (() => {
    if (roleCode === 'HBADMIN') {
      return true;
    }
    if (!required) {
      return true;
    }
    if (Array.isArray(required) && required.length === 0) {
      return true;
    }
    if (typeof required === 'string') {
      return permissionList.includes(required);
    }
    return required.some((perm: unknown) => permissionList.includes(perm));
  })();

  // Treat "Has Token BUT No User" as loading state to prevent premature redirect
  // But if there's already an error, don't stall forever — fall through to permission check
  if (isLoading || (!user && token && !error)) {
    return null;
  }

  if (!hasPermission) {
    // Redirect HBADMIN to dashboard, everyone else to booking-calendar
    const redirectPath = roleCode === 'HBADMIN' ? '/dashboard' : '/sitemap';
    // Avoid redirect loop: if already on the target path, show Forbidden
    if (location.pathname === redirectPath) {
      return <Forbidden />;
    }
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default AuthGuard;
